import type { Scraper } from '../api/types';

/** The design has one four-value health axis; the API has three orthogonal
 *  ones (`status`, `health_label`, `enabled`). This is the single place they
 *  are collapsed — every view derives its chrome from the result.
 *
 *  `running` is a fifth state the prototype lacked. It is worth having: it's
 *  the only genuinely real-time signal in the app. */
export type ScraperState = 'running' | 'healthy' | 'degraded' | 'down' | 'paused';

export function deriveScraperState(s: Scraper): ScraperState {
  if (!s.enabled) return 'paused';
  if (s.status === 'running') return 'running';
  if (s.status === 'error') return 'down';
  if (s.health_label === 'unhealthy') return 'down';
  if (s.health_label === 'watch' || s.slow_warning) return 'degraded';
  return 'healthy';
}

export interface StateMeta {
  label: string;
  /** Modifier class for `.pill` */
  pill: '' | 'indigo' | 'green' | 'yellow' | 'red';
  /** CSS colour for `.sdot` and numeric emphasis */
  dot: string;
  /** Whether the status dot should pulse */
  live: boolean;
}

export const STATE_META: Record<ScraperState, StateMeta> = {
  running: { label: 'Running', pill: 'indigo', dot: 'var(--accent)', live: true },
  healthy: { label: 'Healthy', pill: 'green', dot: 'var(--ok)', live: false },
  degraded: { label: 'Degraded', pill: 'yellow', dot: 'var(--warn)', live: false },
  down: { label: 'Down', pill: 'red', dot: 'var(--err)', live: false },
  paused: { label: 'Paused', pill: '', dot: 'var(--fg-3)', live: false },
};

/** Colour for the health score. Substitutes for the design's fabricated
 *  `uptime %` / `errRate`, neither of which the backend tracks. */
export function healthColor(s: Scraper): string {
  return STATE_META[deriveScraperState(s)].dot;
}

export function isAttentionWorthy(s: Scraper): boolean {
  const state = deriveScraperState(s);
  return state === 'down' || state === 'degraded';
}

/** The design showed a per-scraper webhook. Routing is actually decided by
 *  site name (`site in NSF_STORES`) inside send_raffles, so this is derived,
 *  not configured — the UI must say so rather than imply it's editable. */
export const NSF_STORES = ['LootVault', 'Toymate', 'Pokebox', 'RAMint', 'PopMartAU'];

export function channelForSite(site: string | null | undefined): 'main' | 'nsf' {
  return site && NSF_STORES.includes(site) ? 'nsf' : 'main';
}
